import { useEffect } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { setObservabilityContext } from './observability';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Route pattern of the deepest match, e.g. `/package/:id/`.
 *
 * Every pattern in `paths.ts` is absolute, so the deepest match that declares
 * one is already the full pattern and nothing has to be joined. Layout routes
 * declare no path and are skipped.
 *
 * The pattern is deliberately not derived from the URL: `page.url` keeps the
 * concrete path, and `redactUrl` only masks the identifiers it knows about, so
 * `/package/152184513` reaches the log store as-is and makes every package a
 * distinct value. The matched pattern is the bounded, groupable counterpart.
 */
export const routeOf = (state: AppRouter['state']): string | undefined => {
  for (let i = state.matches.length - 1; i >= 0; i--) {
    const { path } = state.matches[i].route;
    if (path) return path;
  }
  return undefined;
};

/**
 * Publishes the matched route pattern to the observability context, so error
 * records carry `page.route`, and keeps publishing on every navigation.
 *
 * Publishes once up front because `subscribe` only fires on the NEXT
 * navigation, never for the state it is handed. On the hook path the
 * render-phase publish has already covered the landing route by the time this
 * runs; this keeps the function correct on its own for a direct caller.
 *
 * Returns the router's unsubscribe.
 */
export const subscribeObservabilityRoute = (
  router: AppRouter,
): (() => void) => {
  const publish = (state: AppRouter['state']): void => {
    setObservabilityContext({ route: routeOf(state) });
  };

  publish(router.state);
  return router.subscribe(publish);
};

/**
 * Takes the router rather than using `useLocation`, matching `useUrlLocale`:
 * this runs above `RouterProvider`, where the router hooks are not available.
 *
 * The initial route is published during render, not from the effect. A route
 * component that throws while rendering keeps the tree from committing, so no
 * effect ever runs — and a crash on the landing page is exactly the error most
 * worth attributing to a route. Writing to the module-level context here is
 * idempotent, and it reads the router's live state rather than a snapshot, so
 * React re-invoking the render (a discarded or retried concurrent attempt)
 * republishes the current route — never a speculative one.
 *
 * The subscription still lives in an effect, because it needs a cleanup. It
 * publishes once more on mount; same value, no observable difference.
 *
 * Errors raised before this component renders — a third-party tag during
 * document load, or anything thrown while `AppRouter` suspends on the
 * configuration query — still report without a route. `page.url` is the
 * attribute that always survives.
 */
export const useObservabilityRoute = (router: AppRouter): void => {
  setObservabilityContext({ route: routeOf(router.state) });
  useEffect(() => subscribeObservabilityRoute(router), [router]);
};
